import {
  getCometAccountInfo,
  AuthCacher,
  UserSettingsCacher,
} from '@cimpress-technology/web-shell-communication';
import auth from '../utilities/authentication';
import { getDefaultTimezone, TimezoneDetails } from '@/store/timeZoneSlice';

let cachedAccountId: string | null = null;

export const getAccountId = async (): Promise<string | null> => {
  if (cachedAccountId) return cachedAccountId;
  else {
    const account = await getCometAccountInfo();
    cachedAccountId = account.id;
    return cachedAccountId;
  }
};

export const getToken = async (): Promise<string | null> => {
  try {
    if (!isInCometPortal()) {
      return auth.getAccessToken();
    }
    const authCacherPromise = AuthCacher.getAuthCacher();
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('AuthCacher timeout')), 3000); // 3 second timeout
    });

    const authCacher = await Promise.race([authCacherPromise, timeoutPromise]);
    return await authCacher.getToken();
  } catch (error) {
    console.error('Error getting token:', error);
    return null;
  }
};

// Helper function to detect if we're in a Comet Portal
export const isInCometPortal = (): boolean => {
  return window !== window.parent && window.parent !== null;
};

export const isUserLoggedIn = async (): Promise<boolean> => {
  try {
    if (!isInCometPortal()) {
      return auth.isLoggedIn();
    }

    // Add timeout for Comet Portal authentication check
    const authCacherPromise = AuthCacher.getAuthCacher();
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('AuthCacher timeout')), 3000); // 3 second timeout
    });
    const authCacher = await Promise.race([authCacherPromise, timeoutPromise]);
    const authInfo = authCacher?.getAuthInfo();
    if (authInfo && authInfo.accessToken) {
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error checking login status:', error);
    return false;
  }
};

/**
 * Fetches the canonical ID (email) of the logged-in user.
 * @returns canonicalId (email)
 */
export const getCanonicalId = async (): Promise<string | null> => {
  try {
    if (!isInCometPortal()) {
      return auth.getProfile()?.email || null;
    }

    const authCacherPromise = AuthCacher.getAuthCacher();
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('AuthCacher timeout')), 3000); // 3 second timeout
    });

    const authCacher = await Promise.race([authCacherPromise, timeoutPromise]);
    const authInfo = authCacher.getAuthInfo();
    const profile = await authInfo.userProfile;
    return (
      profile['https://claims.cimpress.io/canonical_id'] ||
      profile.email ||
      null
    );
  } catch (error) {
    console.error('Error getting canonical ID:', error);
    return null;
  }
};

const mapOffsetToTimezone = (offset: string): string => {
  // Already a valid IANA identifier (e.g. "America/New_York", "Asia/Calcutta")
  if (offset.includes('/')) return offset;

  switch (offset) {
    case 'UTC+00:00':
    case 'UTC':
    case '+00:00':
      return 'UTC';
    case 'UTC+05:30':
    case '+05:30':
      return 'Asia/Calcutta';
    case 'UTC-05:00':
    case '-05:00':
      return 'America/New_York';
    case 'UTC-08:00':
    case '-08:00':
      return 'America/Los_Angeles';
    case 'UTC+01:00':
    case '+01:00':
      return 'Europe/London';
    case 'UTC+02:00':
    case '+02:00':
      return 'Europe/Berlin';
    case 'UTC-06:00':
    case '-06:00':
      return 'America/Chicago';
    case 'UTC-07:00':
    case '-07:00':
      return 'America/Denver';
    case 'UTC+09:00':
    case '+09:00':
      return 'Asia/Tokyo';
    case 'UTC+08:00':
    case '+08:00':
      return 'Asia/Singapore';

    default:
      return 'UTC'; // Default fallback
  }
};

const buildTimezoneDetails = (tz: string): TimezoneDetails => {
  const validTimezone = mapOffsetToTimezone(tz);
  return {
    value: validTimezone,
    label: `${tz} (${validTimezone})`,
    abbreviation: validTimezone === 'UTC' ? 'UTC' : '',
  };
};

export const getTZ = async (): Promise<TimezoneDetails> => {
  try {
    const userSettingsCacher = await UserSettingsCacher.getUserSettingsCacher();
    const tz = userSettingsCacher.getTimezone();
    if (tz) return buildTimezoneDetails(tz);
  } catch (error) {
    console.error('Error fetching timezone from UserSettingsCacher:', error);
  }
  return getDefaultTimezone();
};

/**
 * Subscribes to live timezone updates pushed from the parent Comet Portal.
 * Returns a handle with an `unsubscribe()` method for cleanup.
 */
export const subscribeTZUpdates = async (
  onUpdate: (tz: TimezoneDetails) => void,
): Promise<{ unsubscribe: () => void }> => {
  try {
    const userSettingsCacher = await UserSettingsCacher.getUserSettingsCacher();
    const subscription = userSettingsCacher
      .onTimezoneUpdated()
      .subscribe((tz: string) => {
        if (tz) onUpdate(buildTimezoneDetails(tz));
      });
    return { unsubscribe: () => subscription.unsubscribe() };
  } catch (error) {
    console.error('Error subscribing to timezone updates:', error);
    return { unsubscribe: () => {} };
  }
};
